import { useEffect, useRef } from 'react';

import { expVal } from '@confluence/feature-experiments';
import { OfficeMacroExperienceTrackerSupport } from '@confluence/fabric-extension-lib/entry-points/OfficeMacroExperienceTrackerSupport.ts';

import { ConnectStaticMacroExperienceTrackerSupport } from './ConnectStaticMacroExperienceTrackerSupport.ts';

type MacroSLOExperienceTrackingData = {
	contentId?: string;
	delayedOutput?: string | null;
	extensionKey?: string;
	isConnectMacro?: boolean;
	isRendererMode: boolean;
	macroId?: string;
	macroOutput?: string | null;
	macroRenderedOutputFromSSR?: unknown;
};

export const useMacroSLOExperienceTracking = ({
	contentId,
	delayedOutput,
	extensionKey,
	isConnectMacro,
	isRendererMode,
	macroId,
	macroOutput,
	macroRenderedOutputFromSSR,
}: MacroSLOExperienceTrackingData) => {
	const connectStaticMacroRenderExperienceResolved = useRef(false);
	const officeMacroRenderExperienceResolved = useRef(false);
	const isUsingServerRenderedMacroOutput = Boolean(macroRenderedOutputFromSSR || macroOutput);
	const hasOfficeMacroRenderedOutput = Boolean(
		delayedOutput || macroRenderedOutputFromSSR || macroOutput,
	);

	useEffect(() => {
		if (
			connectStaticMacroRenderExperienceResolved.current ||
			!isRendererMode ||
			isUsingServerRenderedMacroOutput
		) {
			return;
		}

		const connectStaticMacroRenderData = {
			contentId,
			extensionKey,
			macroId,
			isConnectMacro,
			macroRenderedOutput: delayedOutput,
		};

		if (!expVal('confluence_frontend_macro_slo_metrics', 'isEnabled', false)) {
			return;
		}

		let shouldTrackConnectStaticMacroRender = false;
		try {
			shouldTrackConnectStaticMacroRender =
				ConnectStaticMacroExperienceTrackerSupport.isTrackable(connectStaticMacroRenderData);
		} catch {
			shouldTrackConnectStaticMacroRender = false;
		}

		if (!shouldTrackConnectStaticMacroRender) {
			return;
		}

		try {
			ConnectStaticMacroExperienceTrackerSupport.start(connectStaticMacroRenderData);
			ConnectStaticMacroExperienceTrackerSupport.succeed();
		} catch {
			// Telemetry must never block or alter macro rendering behaviour.
		} finally {
			connectStaticMacroRenderExperienceResolved.current = true;
		}
	}, [
		contentId,
		delayedOutput,
		extensionKey,
		isConnectMacro,
		isRendererMode,
		isUsingServerRenderedMacroOutput,
		macroId,
	]);

	useEffect(() => {
		if (
			officeMacroRenderExperienceResolved.current ||
			!isRendererMode ||
			!expVal('confluence_frontend_macro_slo_metrics', 'isEnabled', false)
		) {
			return;
		}

		const officeMacroRenderData = {
			macroName: extensionKey,
		};

		let shouldTrackOfficeMacroRender = false;
		try {
			shouldTrackOfficeMacroRender =
				OfficeMacroExperienceTrackerSupport.isTrackable(officeMacroRenderData);
		} catch {
			shouldTrackOfficeMacroRender = false;
		}

		if (!shouldTrackOfficeMacroRender) {
			return;
		}

		if (!hasOfficeMacroRenderedOutput) {
			return;
		}

		try {
			OfficeMacroExperienceTrackerSupport.start(officeMacroRenderData);
			OfficeMacroExperienceTrackerSupport.succeed();
		} catch {
			// Telemetry must never block or alter macro rendering behaviour.
		} finally {
			officeMacroRenderExperienceResolved.current = true;
		}
	}, [extensionKey, hasOfficeMacroRenderedOutput, isRendererMode]);
};
